import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CalendarPlus, Loader2, Trash2, Sparkles, MapPin, Wand2, Pencil, X } from "lucide-react";

const EVENT_TYPES_IT = ["concerto", "festival", "festa patronale", "sagra", "manifestazione", "evento sportivo", "fiera", "spettacolo", "congresso", "mostra"];

const scoreColor = (v) => (v >= 66 ? "#16a34a" : v >= 40 ? "#b45309" : "#94a3b8");

const fmtRange = (s, e) => {
  const f = (iso) => { try { const d = new Date(iso); return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`; } catch { return iso; } };
  return e && e !== s ? `${f(s)} – ${f(e)}` : f(s);
};

export default function ManualEvents({ propId, lang = "it" }) {
  const L = (it, en) => (lang === "en" ? en : it);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editScore, setEditScore] = useState("");
  const [form, setForm] = useState({ name: "", type: "", start_date: "", end_date: "", venue: "", distance_km: "", notes: "" });

  const load = useCallback(async () => {
    if (!propId) return;
    setLoading(true);
    try { const { data } = await api.get(`/properties/${propId}/manual-events`); setEvents(data.events || []); }
    catch { setEvents([]); } finally { setLoading(false); }
  }, [propId]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name || !form.start_date) { toast.error(L("Nome e data obbligatori", "Name and date required")); return; }
    setSaving(true);
    try {
      const payload = { ...form, distance_km: form.distance_km ? parseFloat(form.distance_km) : null };
      const { data } = await api.post(`/properties/${propId}/manual-events`, payload);
      toast.success(L(`Evento analizzato — impatto ${data.event.impact_score}/100`, `Event analyzed — impact ${data.event.impact_score}/100`));
      setForm({ name: "", type: "", start_date: "", end_date: "", venue: "", distance_km: "", notes: "" });
      setAdding(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || L("Errore", "Error")); }
    finally { setSaving(false); }
  };

  const saveScore = async (id) => {
    const v = parseInt(editScore, 10);
    if (isNaN(v)) { toast.error(L("Valore non valido", "Invalid value")); return; }
    try {
      await api.put(`/properties/${propId}/manual-events/${id}`, { impact_score: Math.max(0, Math.min(100, v)) });
      toast.success(L("Impatto aggiornato", "Impact updated"));
      setEditId(null); load();
    } catch { toast.error(L("Errore", "Error")); }
  };

  const reEnrich = async (id) => {
    try {
      toast(L("Rianalizzo l'evento…", "Re-analyzing event…"));
      await api.put(`/properties/${propId}/manual-events/${id}`, { impact_score: null, re_enrich: true });
      load();
    } catch { toast.error(L("Errore", "Error")); }
  };

  const remove = async (id) => {
    try { await api.delete(`/properties/${propId}/manual-events/${id}`); load(); }
    catch { toast.error(L("Errore", "Error")); }
  };

  const inputCls = "w-full h-10 rounded-md border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40";

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="bg-card border border-border rounded-lg p-6" data-testid="mp-manual-events">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <div className="flex items-center gap-2"><CalendarPlus className="w-5 h-5 text-primary" /><h2 className="text-lg font-semibold">{L("Eventi manuali", "Manual events")}</h2></div>
        <Button size="sm" variant={adding ? "outline" : "default"} onClick={() => setAdding(!adding)} data-testid="me-toggle-add">
          {adding ? <><X className="w-4 h-4 mr-1.5" />{L("Chiudi", "Close")}</> : <><CalendarPlus className="w-4 h-4 mr-1.5" />{L("Aggiungi evento", "Add event")}</>}
        </Button>
      </div>
      <p className="text-sm text-muted-foreground mb-4">{L("Inserisci eventi locali che conosci (concerti, sagre, fiere…): Revenue AI li analizza e assegna un punteggio di impatto sulla domanda, che puoi correggere a mano. L'impatto entra nel prezzo consigliato.", "Add local events you know (concerts, fairs…): Revenue AI analyzes them and assigns a demand impact score you can override. The impact feeds the recommended price.")}</p>

      {adding && (
        <div className="rounded-lg border border-border bg-muted/30 p-4 mb-5 space-y-3" data-testid="me-add-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input className={inputCls} placeholder={L("Nome evento *", "Event name *")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="me-name" />
            <input className={inputCls} placeholder={L("Tipo (es. concerto)", "Type (e.g. concert)")} list="me-types" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="me-type" />
            <datalist id="me-types">{EVENT_TYPES_IT.map((t) => <option key={t} value={t} />)}</datalist>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{L("Inizio *", "Start *")}</label><input type="date" className={inputCls} value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="me-start" /></div>
            <div><label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{L("Fine", "End")}</label><input type="date" className={inputCls} value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="me-end" /></div>
            <div><label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{L("Distanza km", "Distance km")}</label><input type="number" step="0.5" className={inputCls} value={form.distance_km} onChange={(e) => setForm({ ...form, distance_km: e.target.value })} data-testid="me-distance" /></div>
            <div><label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{L("Luogo", "Venue")}</label><input className={inputCls} value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} data-testid="me-venue" /></div>
          </div>
          <input className={inputCls} placeholder={L("Note (opzionale) — es. artista nazionale, ingresso libero", "Notes (optional)")} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="me-notes" />
          <div className="flex justify-end">
            <Button size="sm" onClick={create} disabled={saving} data-testid="me-submit">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
              {L("Analizza e salva", "Analyze & save")}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-8 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />{L("Carico…", "Loading…")}</div>
      ) : events.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6" data-testid="me-empty">{L("Nessun evento manuale. Aggiungine uno per aiutare l'AI a cogliere i picchi di domanda.", "No manual events yet. Add one to help the AI catch demand peaks.")}</p>
      ) : (
        <div className="space-y-2.5" data-testid="me-list">
          {events.map((ev) => (
            <div key={ev.id} className="flex items-start gap-4 rounded-lg border border-border p-3.5" data-testid={`me-item-${ev.id}`}>
              <div className="text-center shrink-0 w-16">
                {editId === ev.id ? (
                  <div className="flex flex-col items-center gap-1">
                    <input type="number" className="w-14 h-8 text-center rounded border border-border bg-background font-data" value={editScore} onChange={(e) => setEditScore(e.target.value)} data-testid={`me-edit-score-${ev.id}`} />
                    <button className="text-[10px] text-primary font-bold" onClick={() => saveScore(ev.id)} data-testid={`me-save-score-${ev.id}`}>{L("SALVA", "SAVE")}</button>
                  </div>
                ) : (
                  <>
                    <div className="font-data text-2xl font-bold" style={{ color: scoreColor(ev.impact_score) }} data-testid={`me-score-${ev.id}`}>{ev.impact_score ?? "—"}</div>
                    <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Impact</div>
                  </>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm">{ev.name}</span>
                  {ev.type && <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary">{ev.type}</span>}
                  <span className="text-xs text-muted-foreground">{fmtRange(ev.start_date, ev.end_date)}</span>
                  {ev.distance_km != null && <span className="text-[11px] text-muted-foreground inline-flex items-center gap-0.5"><MapPin className="w-3 h-3" />{ev.distance_km} km</span>}
                  {ev.user_override && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600 font-bold">{L("modificato", "overridden")}</span>}
                </div>
                {ev.ai_reasoning && <p className="text-xs text-muted-foreground mt-1">{ev.ai_reasoning}</p>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button title={L("Modifica impatto", "Edit impact")} onClick={() => { setEditId(ev.id); setEditScore(String(ev.impact_score ?? "")); }} className="p-1.5 rounded hover:bg-muted text-muted-foreground" data-testid={`me-edit-${ev.id}`}><Pencil className="w-4 h-4" /></button>
                <button title={L("Rianalizza con AI", "Re-analyze with AI")} onClick={() => reEnrich(ev.id)} className="p-1.5 rounded hover:bg-muted text-muted-foreground" data-testid={`me-reenrich-${ev.id}`}><Wand2 className="w-4 h-4" /></button>
                <button title={L("Elimina", "Delete")} onClick={() => remove(ev.id)} className="p-1.5 rounded hover:bg-muted text-red-500" data-testid={`me-delete-${ev.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}
